import React, { useState, useEffect } from "react";
import sanityClient from "../client";
import { motion } from "framer-motion";
import "./styles/SuccessStories.scss";
import ErrorBoundary from './ErrorBoundary';

export default function SuccessStories() {
  const [stories, setStories] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "successStories"] | order(_createdAt desc) {
          _id,
          name,
          description,
          achievements,
          mediaLinks,
          "images": images[]{
            "url": asset->url,
            "alt": asset->originalFilename
          }
        }`
      )
      .then((data) => {
        setStories(data);
        const initialIndices = {};
        data.forEach(story => {
          initialIndices[story._id] = 0;
        });
        setCurrentImageIndex(initialIndices);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      });
  }, []);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleNextImage = (storyId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [storyId]: (prev[storyId] + 1) % imagesLength
    }));
  };

  const handlePrevImage = (storyId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [storyId]: (prev[storyId] - 1 + imagesLength) % imagesLength
    }));
  };

  return (
    <ErrorBoundary>
      <div className="success-stories">
        <section className="header-section">
          <h1>Our Impact</h1>
          <p>Celebrating the achievements of our talented alumni</p>
        </section>

        <section className="stories-section">
          {loading ? (
            <p>Loading success stories...</p>
          ) : stories.length === 0 ? (
            <p>No success stories found. Please add some through the Sanity studio.</p>
          ) : (
            stories.map((story, index) => {
              return (
                <motion.div
                  key={story._id}
                  className={`story-item ${index % 2 === 0 ? "left" : "right"}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }} // Reduced threshold
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  <h2>{story.name}</h2>
                  <div className="image-carousel">
                    {story.images && story.images.length > 0 ? (
                      <>
                        {story.images.length > 1 && (
                          <button 
                            className="carousel-button left" 
                            onClick={() => handlePrevImage(story._id, story.images.length)}
                          >
                            ❮
                          </button>
                        )}
                        <img
                          src={story.images[currentImageIndex[story._id]]?.url}
                          alt={story.name}
                          onClick={() => handleImageClick(story.images[currentImageIndex[story._id]]?.url)}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/fallback-image.jpg';
                          }}
                        />
                        {story.images.length > 1 && (
                          <button 
                            className="carousel-button right" 
                            onClick={() => handleNextImage(story._id, story.images.length)}
                          >
                            ❯
                          </button>
                        )}
                      </>
                    ) : (
                      <p>No images available</p>
                    )}
                  </div>
                  <div className="content">
                    <p className="description">{story.description}</p>
                    {story.achievements && story.achievements.length > 0 && (
                      <div className="achievements">
                        <h3>Achievements</h3>
                        <ul>
                          {story.achievements.map((achievement, i) => (
                            <li key={i}>{achievement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {story.mediaLinks && story.mediaLinks.length > 0 && (
                      <div className="media-links">
                        <h3>Related Media</h3>
                        <div className="links-grid">
                          {story.mediaLinks.map((link, i) => (
                            <a
                              key={i}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`media-link ${link.type}`}
                            >
                              {link.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
        </section>

        {selectedImage && (
          <div className="lightbox" onClick={() => setSelectedImage(null)}>
            <img src={selectedImage} alt="Full size" />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
} 